import React from 'react';
import { useContent } from './content';
import { IN_APP_MESSAGE, copyLink } from './inapp';
import { SparkIcon } from './icons';
import type { TranscriptEntry } from './types';

export function formatTime(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1_000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <a className={`brand ${compact ? 'brand--compact' : ''}`} href="/" aria-label="Interview Room">
      <span className="brand-mark">
        <span />
        <span />
        <span />
      </span>
      <span className="brand-wordmark">
        <strong>Interview Room</strong>
        {!compact && <small>private practice</small>}
      </span>
    </a>
  );
}

export function Transcript({
  entries,
  emptyText,
  labels = { ai: 'Interviewer', user: 'You' },
  labelFor,
}: {
  entries: TranscriptEntry[];
  emptyText: string;
  labels?: { ai: string; user: string };
  labelFor?: (entry: TranscriptEntry) => string;
}) {
  const endRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [entries]);

  if (entries.length === 0) {
    return <div className="transcript-empty"><SparkIcon /><p>{emptyText}</p></div>;
  }
  return (
    <div className="transcript-list">
      {entries.map((entry) => (
        <article className={`transcript-entry transcript-entry--${entry.role}`} key={entry.id}>
          <header>
            <span>{labelFor ? labelFor(entry) : entry.role === 'interviewer' ? labels.ai : labels.user}</span>
            {entry.status === 'speaking' && <i>speaking</i>}
          </header>
          <p>{entry.text}</p>
        </article>
      ))}
      <div ref={endRef} />
    </div>
  );
}

export function InAppBanner() {
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="inapp-banner" role="alert">
      <p>{IN_APP_MESSAGE}</p>
      <button
        onClick={() => {
          void copyLink().then((ok) => {
            setCopied(ok);
            window.setTimeout(() => setCopied(false), 2_000);
          });
        }}
        type="button"
      >
        {copied ? 'Link copied' : 'Copy link'}
      </button>
    </div>
  );
}

export function TaniaCard({ detailed = false }: { detailed?: boolean }) {
  const tania = useContent()?.taniaProfile;
  if (!tania) return null;
  const headline = `${tania.name}${tania.title ? ` — ${tania.title}` : ''}${tania.company ? `, ${tania.company}` : ''}`;
  const body = (
    <>
      {tania.background && <p>{tania.background}</p>}
      {tania.interviewFocus && <p><b>Focus:</b> {tania.interviewFocus}</p>}
      {tania.likelyQuestions.length > 0 && (
        <ul>{tania.likelyQuestions.map((question, index) => <li key={index}>{question}</li>)}</ul>
      )}
    </>
  );
  if (detailed) {
    return (
      <aside className="tania-card" aria-label="Interviewer profile">
        <p className="eyebrow">Interviewer</p>
        <strong>{headline}</strong>
        {body}
      </aside>
    );
  }
  return (
    <details className="tania-card" aria-label="Interviewer profile">
      <summary>
        <span className="eyebrow">Interviewer</span>
        <strong>{headline}</strong>
        <em>More about Tania</em>
      </summary>
      {body}
    </details>
  );
}

export type Section = 'prep' | 'mock' | 'sessions';

const SECTION_LABELS: ReadonlyArray<{ id: Section; label: string }> = [
  { id: 'prep', label: 'Interview prep' },
  { id: 'mock', label: 'Mock interview' },
  { id: 'sessions', label: 'Sessions & progress' },
];

export function TopNav({
  active,
  onLogout,
  onSelect,
}: {
  active: Section;
  onLogout: () => void;
  onSelect: (section: Section) => void;
}) {
  return (
    <header className="top-nav">
      <Brand compact />
      <nav aria-label="Sections" className="top-nav-tabs">
        {SECTION_LABELS.map((item) => (
          <button
            aria-current={active === item.id ? 'page' : undefined}
            className={active === item.id ? 'is-active' : ''}
            key={item.id}
            onClick={() => onSelect(item.id)}
            type="button"
          >
            {item.label}
          </button>
        ))}
      </nav>
      <button className="text-button" onClick={onLogout} type="button">Lock room</button>
    </header>
  );
}
